import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { CheckCircle } from 'lucide-react'
import { PortalShell } from '@/components/portal/PortalShell'
import { useOnboardingStatus } from '@/hooks/useOnboardingStatus'
import { buildDraftSummarySections } from '@/lib/cdds-draft-summary'
import { cddsFormTitle, cddsParagraphDisplayText } from '@/lib/cdds-paragraph-display'
import {
  cddsReadOnly,
  cddsSubmitDisabled,
  cddsContinueIsSecondary,
  isGrandfatheredCddsParagraph,
  resolveCddsCode,
} from '@/lib/cdds-rules'
import { formatWibDateTime } from '@/lib/utils'
import { ApiError, clientApi, getClientId, type CddsForm, type CddsProgress } from '@/lib/api-client'

const WALK = ['01', '02', '03', '04', '05.2', '06.2', '07', '08', '09', '10']

function savedBranch(progress: CddsProgress): '02.1' | '02.2' {
  const left = progress.signed['02.1']
  const right = progress.signed['02.2']
  if (left && right) return left > right ? '02.1' : '02.2'
  if (right) return '02.2'
  return '02.1'
}

function firstIncomplete(progress: CddsProgress): string {
  for (const step of WALK) {
    if (step === '10') return '10'
    if (step === '02') {
      const left = Boolean(progress.signed['02.1'])
      const right = Boolean(progress.signed['02.2'])
      if (left === right) return '02'
      continue
    }
    if (!progress.signed[step]) return step
  }
  return '10'
}

function stepErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.detail && typeof err.detail === 'object' && !Array.isArray(err.detail)) {
    const detail = err.detail as { missing?: string[]; conflict?: string[] }
    const steps = [...(detail.missing ?? []), ...(detail.conflict ?? [])]
    if (steps.length > 0) return fallback
  }
  return err instanceof Error ? err.message : fallback
}

function nextCode(code: string): string | null {
  const i = WALK.indexOf(code)
  if (i < 0 || i >= WALK.length - 1) return null
  return WALK[i + 1]
}

export function CddsWalkPage() {
  const { t } = useTranslation()
  const { code: rawCode } = useParams()
  const resolved = rawCode ? resolveCddsCode(rawCode) : null
  const code = resolved && resolved !== 'unknown' ? resolved : ''
  const nav = useNavigate()
  const clientId = getClientId()
  const queryClient = useQueryClient()
  const [ticks, setTicks] = useState<string[]>([])
  const [xor, setXor] = useState<'02.1' | '02.2'>('02.1')
  const [xorTouched, setXorTouched] = useState(false)
  const [broker, setBroker] = useState('')
  const [branch, setBranch] = useState('jkt')
  const [localError, setLocalError] = useState<string | null>(null)
  const statusQ = useOnboardingStatus(clientId)
  const catalogQ = useQuery({ queryKey: ['cdds-catalog'], queryFn: () => clientApi.getCddsCatalog() })
  const progQ = useQuery({
    queryKey: ['cdds-progress', clientId],
    queryFn: () => clientApi.getCddsProgress(clientId!),
    enabled: Boolean(clientId),
  })
  const editableStatus = !statusQ.data?.status || ['DRAFT', 'FORM_SUBMITTED'].includes(statusQ.data.status)
  const statusLoading = statusQ.isLoading || statusQ.isFetching
  const draftLocked = Boolean(statusQ.data?.awaiting_approval) || !editableStatus
  const readOnly = cddsReadOnly({
    statusLoading,
    editableStatus,
    code,
    progressComplete: progQ.data?.complete,
    signed10: Boolean(progQ.data?.signed['10']),
    awaitingApproval: Boolean(statusQ.data?.awaiting_approval),
  })
  const displayCode = code === '02' ? xor : code
  const stepSigned = Boolean(progQ.data?.signed[displayCode])
  const draftQ = useQuery({ queryKey: ['wizard-draft'], queryFn: () => clientApi.getDraft() })
  const form = useMemo(
    () => catalogQ.data?.forms.find((f) => f.code === (code === '02' ? xor : code)),
    [catalogQ.data, code, xor],
  )

  const stepIndex = Math.max(0, WALK.indexOf(code))
  const stepTotal = WALK.length
  const walkSigned = WALK.map((step) =>
    step === '02'
      ? Boolean(progQ.data?.signed['02.1'] || progQ.data?.signed['02.2'])
      : Boolean(progQ.data?.signed[step]),
  )

  // Each CDDS step is a new document: open it at the top and move focus to its title.
  const titleRef = useRef<HTMLHeadingElement>(null)
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
    titleRef.current?.focus({ preventScroll: true })
  }, [code])

  // The guard message describes the inputs as they were on the last tap; drop it once they change.
  useEffect(() => {
    setLocalError(null)
  }, [ticks, broker, branch, xor])

  useEffect(() => {
    if (!rawCode) {
      if (!progQ.data) return
      nav(`/register/cdds/${firstIncomplete(progQ.data)}`, { replace: true })
      return
    }
    const next = resolveCddsCode(rawCode)
    if (next === 'unknown') {
      if (!progQ.data) return
      nav(`/register/cdds/${firstIncomplete(progQ.data)}`, { replace: true })
      return
    }
    if (next !== rawCode) nav(`/register/cdds/${next}`, { replace: true })
  }, [rawCode, nav, progQ.data])

  useEffect(() => {
    if (!progQ.data || xorTouched) return
    setXor(savedBranch(progQ.data))
  }, [progQ.data, xorTouched])

  useEffect(() => {
    const answers = progQ.data?.answers ?? {}
    const target = code === '02' ? xor : code
    const extra = answers[target] ?? {}
    setTicks(Array.isArray(extra.ticks) ? extra.ticks.map(String) : [])
    if (typeof extra.previous_broker_name === 'string') setBroker(extra.previous_broker_name)
    if (typeof extra.nearest_branch_id === 'string') setBranch(extra.nearest_branch_id)
  }, [code, xor, progQ.data])

  const goNext = () => {
    const n = nextCode(code)
    if (n) nav(`/register/cdds/${n}`)
    else nav('/dashboard')
  }

  const ya = useMutation({
    mutationFn: async () => {
      if (!clientId) throw new Error('No client')
      if (code === '10') {
        await clientApi.submitCdds(clientId)
        return
      }
      const target = code === '02' ? xor : code
      const extra: Record<string, unknown> = {}
      if (target === '02.2') extra.previous_broker_name = broker.trim()
      if (target === '06.2') extra.nearest_branch_id = branch
      await clientApi.postCddsYa(clientId, target, { ticks, extra })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['cdds-progress', clientId] })
      if (code === '10') {
        await queryClient.invalidateQueries({ queryKey: ['client', 'status'] })
      }
      goNext()
    },
  })

  const guard = (): string | null => {
    if (code === '10') return null
    const target = code === '02' ? xor : code
    const kind = catalogQ.data?.forms.find((f) => f.code === target)?.kind
    const paras = catalogQ.data?.forms.find((f) => f.code === target)?.paragraphs ?? []
    if ((kind === 'ticks_ya' || kind === 'ticks_branch_ya') && paras.some((_, i) => !ticks.includes(String(i)))) {
      return t('cdds.ticksRequired')
    }
    if (target === '02.2' && !broker.trim()) return t('cdds.brokerRequired')
    if (target === '06.2' && !branch) return t('cdds.branchRequired')
    return null
  }

  const confirmLabel = code === '10' ? t('cdds.submitApplication') : t('cdds.confirmYes')
  const showCdds10Submitted = code === '10' && readOnly
  const continueSecondary = cddsContinueIsSecondary({ code, stepSigned })

  return (
    <PortalShell>
      <div className="prod-page-content prod-page-content--cdds">
        <article className="prod-ack-card prod-dashboard-card">
          <header className="prod-ack-card__header">
            <h1 ref={titleRef} tabIndex={-1} className="prod-text-page-title prod-text-page-title--left">
              {form ? cddsFormTitle(t, form.code, form.title) : `CDDS.${code}`}
            </h1>
            <p className="prod-ack-card__step">
              {t('cdds.stepProgress', {
                code: code === '02' ? xor : code,
                current: stepIndex + 1,
                total: stepTotal,
              })}
            </p>
            <div className="prod-segments prod-ack-card__segments" aria-hidden="true">
              {WALK.map((step, index) => (
                <span
                  key={step}
                  className={[
                    'prod-segments__item',
                    walkSigned[index] ? 'prod-segments__item--done' : '',
                    !walkSigned[index] && index === stepIndex ? 'prod-segments__item--current' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                />
              ))}
            </div>
          </header>
          <div className="prod-ack-card__body prod-prose">
            <CddsXorPicker
              code={code}
              xor={xor}
              readOnly={readOnly}
              setXor={(value) => {
                setXorTouched(true)
                setXor(value)
              }}
            />
            {code === '04' ? (
              <CddsDraftSummary
                draft={draftQ.data?.draft_data}
                clientStatus={statusQ.data?.status}
              />
            ) : null}
            {code === '10' ? (
              <CddsCompleteness progress={progQ.data} forms={catalogQ.data?.forms ?? []} />
            ) : null}
            <CddsParagraphs
              form={form}
              ticks={ticks}
              setTicks={setTicks}
              readOnly={readOnly}
              stepSigned={stepSigned}
            />
            <CddsExtras
              form={form}
              broker={broker}
              setBroker={setBroker}
              branch={branch}
              setBranch={setBranch}
              branches={catalogQ.data?.branches ?? []}
              readOnly={readOnly}
            />
          </div>
          <footer className="prod-ack-card__footer">
            {localError || ya.error ? (
              <p className="prod-error prod-ack-card__error" role="alert">
                {localError ||
                  stepErrorMessage(
                    ya.error,
                    t('cdds.missingSteps', {
                      steps: [
                        ...(((ya.error as ApiError).detail as { missing?: string[] } | undefined)?.missing ?? []),
                        ...(((ya.error as ApiError).detail as { conflict?: string[] } | undefined)?.conflict ?? []),
                      ].join(', '),
                    }),
                  )}
              </p>
            ) : null}
            {showCdds10Submitted ? (
              <p className="prod-cdds-submitted-status" role="status">
                <CheckCircle className="prod-cdds-submitted-status__icon size-5" aria-hidden />
                {t('cdds.applicationSubmitted')}
              </p>
            ) : (
            <button
              type="button"
              className={
                continueSecondary
                  ? 'prod-btn prod-btn--outline prod-btn--gold-full'
                  : 'prod-btn prod-btn--gold prod-btn--gold-full'
              }
              onClick={() => {
                const blocked = guard()
                setLocalError(blocked)
                if (!blocked) ya.mutate()
              }}
              disabled={cddsSubmitDisabled({
                yaPending: ya.isPending,
                readOnly,
                code,
                progressComplete: progQ.data?.complete,
              })}
            >
              {confirmLabel}
            </button>
            )}
          </footer>
          {readOnly && progQ.data?.signed[code === '02' ? xor : code] ? (
            <p className="prod-ack-card__complete prod-auth__subtitle">
              {t('cdds.approvedOn', {
                date: formatWibDateTime(progQ.data.signed[code === '02' ? xor : code]),
              })}
            </p>
          ) : null}
          {progQ.data?.complete ? (
            <p className="prod-ack-card__complete prod-auth__subtitle">{t('cdds.allComplete')}</p>
          ) : null}
        </article>
      </div>
    </PortalShell>
  )
}

const CDDS_TABLE_STEPS = ['01', '02', '03', '04', '05.2', '06.2', '07', '08', '09'] as const

function CddsDraftSummary({
  draft,
  clientStatus,
}: {
  draft?: Record<string, unknown>
  clientStatus?: string
}) {
  const { t } = useTranslation()
  const sections = buildDraftSummarySections(t, draft)
  return (
    <div className="prod-mt-md prod-cdds-summary">
      {sections.map((section) => (
        <section key={section.title} className="prod-cdds-summary__block">
          <h2 className="prod-field__label">{section.title}</h2>
          <dl className="prod-cdds-summary__list">
            {section.rows.map((row) => (
              <div key={row.label} className="prod-cdds-summary__row">
                <dt>{row.label}</dt>
                <dd>{row.value}</dd>
              </div>
            ))}
          </dl>
        </section>
      ))}
      {clientStatus === 'DRAFT' ? (
        <Link to="/register/data" className="prod-link">
          {t('cdds.changeData')}
        </Link>
      ) : null}
    </div>
  )
}

function resolveSignedStep(progress: CddsProgress | undefined, step: string): { code: string; at?: string } {
  if (step !== '02') {
    const at = progress?.signed[step]
    return { code: step, at: at || undefined }
  }
  if (progress?.signed['02.1']) return { code: '02.1', at: progress.signed['02.1'] }
  if (progress?.signed['02.2']) return { code: '02.2', at: progress.signed['02.2'] }
  return { code: '02' }
}

function CddsCompleteness({
  progress,
  forms,
}: {
  progress?: CddsProgress
  forms: CddsForm[]
}) {
  const { i18n } = useTranslation()
  const tId = i18n.getFixedT('id')
  const titleFor = (formCode: string) => {
    const row = forms.find((f) => f.code === formCode)
    if (!row) return `CDDS.${formCode}`
    return row.title
  }
  return (
    <div className="prod-mt-md prod-table-wrap">
      <p>{tId('pbk.tableSubtitle')}</p>
      <p>{tId('pbk.declaration')}</p>
      <table className="prod-table prod-table--stack">
        <thead>
          <tr>
            <th>{tId('pbk.colNo')}</th>
            <th>{tId('pbk.colForm')}</th>
            <th>{tId('pbk.colProcess')}</th>
            <th>{tId('pbk.colStatus')}</th>
            <th>{tId('pbk.colDate')}</th>
          </tr>
        </thead>
        <tbody>
          {CDDS_TABLE_STEPS.map((step, index) => {
            const { code, at } = resolveSignedStep(progress, step)
            return (
              <tr key={step}>
                <td data-label={tId('pbk.colNo')} className="prod-num">{index + 1}</td>
                <td data-label={tId('pbk.colForm')}>{titleFor(code)}</td>
                <td data-label={tId('pbk.colProcess')}>{tId('cdds.processOnline')}</td>
                <td data-label={tId('pbk.colStatus')}>{at ? tId('cdds.statusSigned') : tId('cdds.notSigned')}</td>
                <td data-label={tId('pbk.colDate')} className="prod-num">{at ? formatWibDateTime(at) : '-'}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function CddsXorPicker({
  code,
  xor,
  setXor,
  readOnly,
}: {
  code: string
  xor: '02.1' | '02.2'
  setXor: (v: '02.1' | '02.2') => void
  readOnly?: boolean
}) {
  const { t } = useTranslation()
  if (code !== '02') return null
  return (
    <fieldset className="prod-choice-field prod-mt-md" disabled={readOnly}>
      <legend className="prod-field__label">{t('cdds.choosePath')}</legend>
      <div className="prod-radio-group prod-radio-group--stack">
        <label className="prod-radio prod-radio--option">
          <input type="radio" name="cdds-02" checked={xor === '02.1'} onChange={() => setXor('02.1')} />
          <span className="prod-radio__body">
            <span className="prod-radio__title">{t('cdds.simulation')}</span>
            <span className="prod-radio__desc">{t('cdds.title.02_1')}</span>
          </span>
        </label>
        <label className="prod-radio prod-radio--option">
          <input type="radio" name="cdds-02" checked={xor === '02.2'} onChange={() => setXor('02.2')} />
          <span className="prod-radio__body">
            <span className="prod-radio__title">{t('cdds.experience')}</span>
            <span className="prod-radio__desc">{t('cdds.title.02_2')}</span>
          </span>
        </label>
      </div>
    </fieldset>
  )
}

function CddsExtras({
  form,
  broker,
  setBroker,
  branch,
  setBranch,
  branches,
  readOnly,
}: {
  form: CddsForm | undefined
  broker: string
  setBroker: (v: string) => void
  branch: string
  setBranch: (v: string) => void
  branches: { id: string; label: string }[]
  readOnly?: boolean
}) {
  const { t } = useTranslation()
  if (form?.code === '02.2') {
    return (
      <label className="prod-field prod-mt-md">
        {t('cdds.previousBroker')}
        <input
          className="prod-input"
          value={broker}
          disabled={readOnly}
          onChange={(e) => setBroker(e.target.value)}
        />
      </label>
    )
  }
  if (form?.code !== '06.2') return null
  // ponytail: radio cards while the branch list is short enough to scan; a long list keeps the select.
  if (branches.length > 0 && branches.length <= 8) {
    return (
      <fieldset className="prod-choice-field prod-mt-md" disabled={readOnly}>
        <legend className="prod-field__label">{t('cdds.nearestBranch')}</legend>
        <div className="prod-radio-group prod-radio-group--stack">
          {branches.map((b) => (
            <label key={b.id} className="prod-radio prod-radio--option">
              <input
                type="radio"
                name="cdds-branch"
                value={b.id}
                checked={branch === b.id}
                onChange={() => setBranch(b.id)}
              />
              <span className="prod-radio__title">{b.label}</span>
            </label>
          ))}
        </div>
      </fieldset>
    )
  }
  return (
    <label className="prod-field prod-mt-md">
      {t('cdds.nearestBranch')}
      <select
        className="prod-input"
        value={branch}
        disabled={readOnly}
        onChange={(e) => setBranch(e.target.value)}
      >
        {branches.map((b) => (
          <option key={b.id} value={b.id}>
            {b.label}
          </option>
        ))}
      </select>
    </label>
  )
}

function CddsParagraphs({
  form,
  ticks,
  setTicks,
  readOnly,
  stepSigned,
}: {
  form: CddsForm | undefined
  ticks: string[]
  setTicks: (v: string[]) => void
  readOnly?: boolean
  stepSigned?: boolean
}) {
  const { t } = useTranslation()
  const paras = form?.paragraphs ?? []
  if (paras.length === 0) return null
  const needTicks = form?.kind === 'ticks_ya' || form?.kind === 'ticks_branch_ya'
  return (
    <ul className="prod-prose">
      {paras.map((p, i) => {
        const text = cddsParagraphDisplayText(p)
        const key = typeof p === 'object' && p && 'id' in p ? p.id : i
        const grandfathered = isGrandfatheredCddsParagraph({
          readOnly: Boolean(readOnly),
          stepSigned: Boolean(stepSigned),
          needTicks,
          index: i,
          ticks,
        })
        return (
        <li key={key}>
          {needTicks && !grandfathered ? (
            <label>
              <input
                type="checkbox"
                disabled={readOnly}
                checked={ticks.includes(String(i))}
                onChange={(e) => {
                  const tickKey = String(i)
                  setTicks(e.target.checked ? [...ticks, tickKey] : ticks.filter((x) => x !== tickKey))
                }}
              />{' '}
              {text}
            </label>
          ) : (
            <>
              {text}
              {grandfathered ? (
                <p className="prod-auth__subtitle prod-mt-sm">{t('cdds.grandfatheredParagraph')}</p>
              ) : null}
            </>
          )}
        </li>
      )})}
    </ul>
  )
}
